import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

interface MemberCharge {
  id: string;
  amount_vatu: number;
  payment_status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  member: {
    id: string;
    full_name: string;
    email: string;
  } | null;
  group: {
    id: string;
    name: string;
  } | null;
}

interface MemberChargesListProps {
  groupId?: string | null;
  showGroupName?: boolean;
}

const MemberChargesList: React.FC<MemberChargesListProps> = ({ 
  groupId, 
  showGroupName = false 
}) => {
  const { user, userProfile } = useAuth();
  const [charges, setCharges] = useState<MemberCharge[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  useEffect(() => {
    fetchCharges();
  }, [groupId, filter, user]);

  const fetchCharges = async () => {
    if (!user) {
      console.log('[MemberChargesList] No user found');
      return;
    }

    console.log('[MemberChargesList] Fetching charges for user:', user.id, 'groupId:', groupId);

    try {
      setLoading(true);
      
      let query = supabase
        .from('payment_transactions')
        .select(`
          id,
          amount_vatu,
          payment_status,
          created_at,
          member:member_id (
            id,
            full_name,
            email
          ),
          group:group_id (
            id,
            name
          )
        `)
        .eq('payment_type', 'member_addition')
        .order('created_at', { ascending: false });

      // Filter by group if provided
      if (groupId) {
        query = query.eq('group_id', groupId);
      } else if (userProfile?.role !== 'super_admin') {
        // For group admins, only show charges for their groups
        // Get groups where user is creator or admin
        const { data: userGroups } = await supabase
          .from('groups')
          .select('id')
          .eq('created_by', user.id);

        const { data: adminGroups } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', user.id)
          .in('role', ['admin', 'clerk']);

        const groupIds = new Set<string>();
        if (userGroups) userGroups.forEach(g => groupIds.add(g.id));
        if (adminGroups) adminGroups.forEach(g => groupIds.add(g.group_id));

        if (groupIds.size > 0) {
          query = query.in('group_id', Array.from(groupIds));
        } else {
          setCharges([]);
          setLoading(false);
          return;
        }
      }

      // Apply status filter
      if (filter !== 'all') {
        query = query.eq('payment_status', filter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[MemberChargesList] Error fetching charges:', error);
        throw error;
      }

      console.log('[MemberChargesList] Charges found:', data?.length || 0, data);
      setCharges((data || []) as MemberCharge[]);
    } catch (error) {
      console.error('[MemberChargesList] Error fetching member charges:', error);
      setCharges([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'text-success bg-success/10';
      case 'rejected':
        return 'text-destructive bg-destructive/10';
      case 'pending':
      default:
        return 'text-warning bg-warning/10';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return 'CheckCircle';
      case 'rejected':
        return 'XCircle';
      case 'pending':
      default:
        return 'Clock';
    }
  };

  const totalPending = charges.filter(c => c.payment_status === 'pending').length;
  const totalAmount = charges.reduce((sum, c) => sum + Number(c.amount_vatu || 0), 0);
  const pendingAmount = charges
    .filter(c => c.payment_status === 'pending')
    .reduce((sum, c) => sum + Number(c.amount_vatu || 0), 0);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-md p-6">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading member charges...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-md p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Member Addition Charges</h3>
          <p className="text-sm text-muted-foreground">
            Charges for adding members to your group (150 VT per member)
          </p>
        </div>
        <Icon name="DollarSign" size={24} className="text-primary" />
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-muted/50 rounded-md p-4">
          <div className="text-xs text-muted-foreground mb-1">Total Charges</div>
          <div className="text-lg font-semibold text-foreground">{charges.length}</div>
        </div>
        <div className="bg-warning/10 rounded-md p-4">
          <div className="text-xs text-muted-foreground mb-1">Pending Payment</div>
          <div className="text-lg font-semibold text-warning">{totalPending}</div>
        </div>
        <div className="bg-primary/10 rounded-md p-4">
          <div className="text-xs text-muted-foreground mb-1">Pending Amount</div>
          <div className="text-lg font-semibold text-primary">
            {pendingAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })} VT
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <label className="text-xs font-medium text-foreground">Filter:</label>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as any)}
          className="px-3 py-1.5 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Charges List */}
      {charges.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Receipt" size={48} className="mx-auto mb-2 opacity-50" />
          <p>No member charges found</p>
          <p className="text-xs mt-1">
            Charges will appear here when members are added to groups on pay-as-you-go plans
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {charges.map((charge) => (
            <div
              key={charge.id}
              className="flex items-center justify-between p-4 border border-border rounded-md hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  {showGroupName && charge.group && (
                    <span className="text-sm font-medium text-foreground">
                      {charge.group.name}
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(charge.payment_status)}`}
                  >
                    <Icon name={getStatusIcon(charge.payment_status)} size={14} />
                    {charge.payment_status.toUpperCase()}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">
                  {charge.member ? (
                    <>
                      Member: <span className="font-medium text-foreground">{charge.member.full_name || charge.member.email}</span>
                    </>
                  ) : (
                    'Member (deleted)'
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {new Date(charge.created_at).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
              <div className="text-right ml-4">
                <div className="text-lg font-semibold text-foreground">
                  {Number(charge.amount_vatu).toLocaleString('en-US', { minimumFractionDigits: 2 })} VT
                </div>
                {charge.payment_status === 'pending' && (
                  <div className="text-xs text-muted-foreground mt-1">Payment required</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MemberChargesList;
